import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
} from '@angular/core';
import { UTCDateRange } from '@epicuro-next/ataraxia/models';
import {
  addDays,
  getDateOptions,
  lastDayOfMonthUTC,
} from '@epicuro-next/utilities';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { DateTime, Interval } from 'luxon';
import { merge, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { ngbDateToDate, ngbDateToUTC, UTCtoNGB } from '../../utils';
import { DatePicker } from '../date-picker';

@Component({
  selector: 'atx-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DateRangePickerComponent extends DatePicker {
  private rangeSelection$$ = new Subject<UTCDateRange | void>();

  @Input() public displayMonths = 1;
  @Input() public displayFooterHandlers = false;
  @Input() public minDate = new Date();
  @Input() public maxDate: Date;
  @Input() public pickerClass: string;
  @Input() public useResetText = false;
  @Input() public validRanges: Interval[] = [];
  @Input() public useUTCDayTimeConversion = false;

  @Input()
  public set range(range: UTCDateRange | null) {
    if (!range) {
      this.fromDate = null;
      this.toDate = null;
      return;
    }

    this.fromDate = UTCtoNGB(range.from ?? null);
    this.toDate = UTCtoNGB(range.to ?? null);

    if (this.fromDate) {
      this.datePicker.navigateTo(this.fromDate);
    }
  }

  @Input()
  public rangeBoundary = 0;

  @Output()
  public rangeSelect = merge(
    this.rangeSelection$$,
    this.monthSelect$.pipe(
      map(
        (date) =>
          ({
            from: date,
            to: lastDayOfMonthUTC(date),
          } as UTCDateRange),
      ),
    ),
  );

  public fromDate: NgbDate | null;
  public toDate: NgbDate | null = null;

  public hoveredDate: NgbDate | null = null;

  public onDateSelection(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;

      this.resetMaxDate();
      return;
    }

    if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;

      if (!this.displayFooterHandlers) {
        this.rangeSelection$$.next({
          from: ngbDateToUTC(this.fromDate, this.useUTCDayTimeConversion),
          to: ngbDateToUTC(this.toDate, this.useUTCDayTimeConversion, true),
        });
      }

      return;
    }

    this.toDate = null;
    this.fromDate = date;
    this.resetMaxDate();
  }

  private isHovered(date: NgbDate) {
    return (
      this.fromDate &&
      !this.toDate &&
      this.hoveredDate &&
      date.after(this.fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  private isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  public isRange(date: NgbDate) {
    return this.isFromDate(date) || this.isToDate(date);
  }

  /**
   * true if the date is the fromDate or the toDate
   * @param date
   */
  public isInRange(date: NgbDate) {
    return this.isRange(date) || this.isInside(date) || this.isHovered(date);
  }

  public isFromDate(date: NgbDate) {
    return date.equals(this.fromDate);
  }

  public isToDate(date: NgbDate) {
    return date.equals(this.toDate);
  }

  public isToPreselect(date: NgbDate) {
    return (
      this.fromDate &&
      !this.toDate &&
      this.hoveredDate &&
      date.after(this.fromDate) &&
      date.equals(this.hoveredDate)
    );
  }

  public onSelect() {
    if (!this.toDate) {
      this.toDate = this.fromDate;
    }
    const dateRange = {
      from: ngbDateToUTC(
        this.fromDate as NgbDate,
        this.useUTCDayTimeConversion,
      ),
      to: ngbDateToUTC(
        this.toDate as NgbDate,
        this.useUTCDayTimeConversion,
        true,
      ),
    };

    this.rangeSelection$$.next(dateRange);
  }

  public resetRangeSelection() {
    this.fromDate = null;
    this.toDate = null;
    this.rangeSelection$$.next();
  }

  // has to be called from template so this is fixed
  // `.bind(this)` in constructor does not work
  public isDisabledCb(date: NgbDate) {
    if (!this.validRanges.length) {
      return false;
    }

    // has to set hours to something, as default is 0 and as a
    // dateRanges also build from 0 it would not be contained
    // "12" is random number and influence nothing
    const dateObj = DateTime.fromObject({ ...date, zone: 'utc' });
    const { year, month, day } = getDateOptions(dateObj);

    return !this.validRanges.some((validRange) => {
      const {
        year: startYear,
        month: startMonth,
        day: startDay,
      } = getDateOptions(validRange.start);
      const {
        year: endYear,
        month: endMonth,
        day: endDay,
      } = getDateOptions(validRange.end);
      return (
        validRange.contains(dateObj) ||
        (startYear === year && startMonth === month && startDay === day) ||
        (endYear === year && endMonth === month && endDay === day)
      );
    });
  }

  private resetMaxDate() {
    if (this.rangeBoundary) {
      this.maxDate = addDays(
        this.fromDate ? ngbDateToDate(this.fromDate) : this.minDate,
        this.rangeBoundary,
      );
    }
  }
}
